/**
 * CustomerShipments — list of parcels the citizen sent / received.
 *
 * Backed by the live citizen-scoped index endpoint:
 *   GET /v1/mail-items/me?role=all&page=1&pageSize=20
 *
 * The endpoint is RBAC-gated on `Parcel/read-own` (seeded to the `customer`
 * role by `prisma/identity/db-migrations/20260507_customer_role.sql`), so
 * every signed-in citizen can call it. Receiver-side items don't surface
 * yet — operator-created mail items don't embed a recipient customerId
 * (the F-002 normalised customer FK lands later); the role filter UI is
 * therefore deliberately omitted from this slice.
 *
 * The "look up by tracking number" form is preserved as a secondary
 * affordance for parcels that aren't on the citizen's account (gifts,
 * forwarded items, etc.).
 */
import { useState, type FormEvent, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Package, Search } from 'lucide-react';

import {
  listMyMailItems,
  type MailItemSummary,
} from '../../lib/api/mail-items';
import { isValidTrackingNumber, normaliseTrackingNumber } from '../track/upu-s10';
import { PageHeader, StatusBadge } from './PortalUI';

const PAGE_SIZE = 20;

function formatCreatedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function CustomerShipments(): JSX.Element {
  const navigate = useNavigate();
  const [tracking, setTracking] = useState<string>('');
  const normalised = normaliseTrackingNumber(tracking);
  const isValid = isValidTrackingNumber(normalised);

  const shipmentsQuery = useQuery({
    queryKey: ['mail-items', 'me', { role: 'all', page: 1, pageSize: PAGE_SIZE }],
    queryFn: () => listMyMailItems({ role: 'all', page: 1, pageSize: PAGE_SIZE }),
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!isValid) {
      return;
    }
    navigate(`/portal/tracking?tn=${encodeURIComponent(normalised)}`);
  }

  return (
    <div>
      <PageHeader title="My Shipments" description="View and track all your shipments" />

      <ShipmentsList
        loading={shipmentsQuery.isLoading}
        error={shipmentsQuery.error}
        items={shipmentsQuery.data?.items ?? []}
      />

      <div className="bg-white rounded-xl border border-border p-6 mt-6">
        <h2 className="text-sm font-semibold text-gray-900 mb-2">
          Look up a different shipment
        </h2>
        <p className="text-xs text-muted mb-4">
          Tracking numbers are 13 characters in the UPU S10 format, e.g.{' '}
          <span className="font-mono">EE123456785NG</span>.
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3" noValidate>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
            <input
              type="text"
              autoComplete="off"
              spellCheck={false}
              placeholder="Enter tracking number"
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 text-sm font-mono uppercase border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
              aria-label="Tracking number"
            />
          </div>
          <button
            type="submit"
            disabled={!isValid}
            className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark transition-colors disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            Track <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ShipmentsList — loading / error / empty / populated states.
// ---------------------------------------------------------------------------

interface ShipmentsListProps {
  readonly loading: boolean;
  readonly error: unknown;
  readonly items: readonly MailItemSummary[];
}

function ShipmentsList({ loading, error, items }: ShipmentsListProps): JSX.Element {
  if (loading) {
    return (
      <div
        className="bg-white rounded-xl border border-border p-6"
        role="status"
        aria-live="polite"
      >
        <div className="space-y-3">
          {[0, 1, 2].map((k) => (
            <div
              key={k}
              className="h-16 rounded-lg bg-slate-100 animate-pulse"
              aria-hidden="true"
            />
          ))}
        </div>
        <p className="sr-only">Loading your shipments…</p>
      </div>
    );
  }

  if (error) {
    const msg =
      error instanceof Error ? error.message : 'Could not load your shipments.';
    return (
      <div
        className="bg-white rounded-xl border border-danger-light p-6"
        role="alert"
        data-testid="shipments-error"
      >
        <p className="text-sm font-semibold text-danger mb-1">
          We couldn&apos;t load your shipments.
        </p>
        <p className="text-xs text-muted">{msg}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        className="bg-white rounded-xl border border-border p-12 text-center"
        data-testid="shipments-empty"
      >
        <Package className="w-10 h-10 text-muted mx-auto mb-3" />
        <p className="text-sm font-semibold text-gray-700 mb-1">No shipments yet</p>
        <p className="text-xs text-muted mb-4">
          Once you book a parcel through the portal, it&apos;ll show up here.
        </p>
        <Link
          to="/portal/parcel-booking"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark transition-colors"
        >
          <Package className="w-4 h-4" /> Book a parcel
        </Link>
      </div>
    );
  }

  return (
    <ul
      className="bg-white rounded-xl border border-border divide-y divide-border overflow-hidden"
      data-testid="shipments-list"
    >
      {items.map((item) => (
        <ShipmentRow key={item.id} item={item} />
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// ShipmentRow
// ---------------------------------------------------------------------------

function ShipmentRow({ item }: { readonly item: MailItemSummary }): JSX.Element {
  const trackHref = `/portal/tracking?tn=${encodeURIComponent(item.trackingNumber)}`;
  const sender = item.senderName ?? 'Unknown sender';
  const receiver = item.receiverName ?? 'Unknown recipient';
  const roleLabel = item.role === 'SENDER' ? 'Sent' : 'Received';

  return (
    <li
      className="p-4 hover:bg-slate-50 transition-colors"
      data-testid="shipment-row"
      data-tracking-number={item.trackingNumber}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <StatusBadge status={item.status} />
            <span className="text-[11px] uppercase tracking-wide text-muted font-medium">
              {roleLabel}
            </span>
          </div>
          <Link
            to={trackHref}
            className="block font-mono text-sm font-semibold text-nipost-green hover:underline"
            data-testid="shipment-tracking-link"
          >
            {item.trackingNumber}
          </Link>
          <p className="text-xs text-gray-700 mt-1 truncate">
            <span className="font-medium">{sender}</span>
            <span className="text-muted mx-1.5">&rarr;</span>
            <span className="font-medium">{receiver}</span>
          </p>
          <p className="text-[11px] text-muted mt-0.5">
            {formatCreatedAt(item.createdAt)} &middot; {item.serviceClass.replace(/_/g, ' ')}
          </p>
        </div>
        <Link
          to={trackHref}
          className="inline-flex items-center gap-1 text-xs font-semibold text-nipost-green hover:text-nipost-green-dark sm:shrink-0"
        >
          Track <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </li>
  );
}
